import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux"; // Import useDispatch hook
import { loginSuccess } from "./store/slices/adLoginSlice"; // Import the loginSuccess action
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function LoginFA() {
  const [credentials, setCredentials] = useState({ Phone: "", Password: "" });
  const [isLoginDisabled, setIsLoginDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add isLoading state
  const [timer, setTimer] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch(); // Initialize useDispatch

// Check if the rate limit was previously hit and if time has passed
  useEffect(() => {
    // Clear any previous timer if the user changes the phone number
    if (timer) clearTimeout(timer);

    if (!credentials.Phone) {
      setIsLoginDisabled(false);
      return; // No phone number, so button should be enabled
    }

    const lockTime = localStorage.getItem(`lockTime_${credentials.Phone}`);
    if (lockTime) {
      const timeElapsed = Date.now() - lockTime;
      const remainingTime = 900000 - timeElapsed; // 15 minutes

      if (remainingTime > 0) {
        setIsLoginDisabled(true);
        startTimer(remainingTime, credentials.Phone); // Start timer for the remaining duration
      } else {
        localStorage.removeItem(`lockTime_${credentials.Phone}`);
        setIsLoginDisabled(false);
      }
    } else {
      setIsLoginDisabled(false);
    }

    // Cleanup function to clear timer when component unmounts
    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials.Phone]); // Dependency: runs when data.Phone changes


  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  // Function to start the timer and disable the button
  const startTimer = (duration) => {
    setIsLoginDisabled(true);
    setTimer(setTimeout(() => {
      setIsLoginDisabled(false);
      localStorage.removeItem('lockTime');
    }, duration));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Start the loading spinner

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/adminLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          dispatch(loginSuccess()); // Dispatch loginSuccess to update the state

          toast.success("Login successful!", {
            position: "top-center",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
          setTimeout(() => navigate("/adminPage"), 1000);
        } else {
          toast.error("Invalid phone number or password.", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      } else if (response.status === 429) {
        // Rate limiting response
        toast.error("Too many login attempts. Please try after 15 minutes.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        // Save lock time in localStorage
        localStorage.setItem('lockTime', Date.now());

        // Disable the login button and start the timer
        startTimer(900000); // 15 minutes in milliseconds
      } else {
        toast.error("Invalid phone number or password.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      console.error("Error during login:", error);
      toast.error("Network error. Please try again later.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false); // Stop the loading spinner after the request
    }
  };

  useEffect(() => {
    return () => clearTimeout(timer); // Clear the timer when the component unmounts
  }, [timer]);

  return (
    <div className="container d-flex justify-content-center align-items-center vh-50">
      <form className="form-group w-50 mt-5 mb-5" onSubmit={handleLogin}>
        <label htmlFor="whnumInput">WhatsApp Number</label>
        <input
          type="tel"
          pattern="(\+?[0-9]{1,4})?[0-9]{10,14}"
          title="Please enter a valid WhatsApp number."
          name="Phone"
          id="whnumInput"
          className="form-control mb-2"
          placeholder="+91XXXXXXXXXX"
          onChange={handleChange}
          required
          disabled={isLoginDisabled} // Disable input during lockout period
        />
        <label htmlFor="passwordInput">Password</label>
        <input
          type="password"
          name="Password"
          id="passwordInput"
          minLength="8"
          className="form-control mb-2"
          placeholder="Type your password"
          onChange={handleChange}
          required
          disabled={isLoginDisabled} // Disable input during lockout period
        />
        <div className="justify-content-center d-flex">
          <button
            type="submit"
            className="btn btn-success btn-lg rounded-pill px-5 py-3 mt-4 mb-4 shadow"
            disabled={isLoginDisabled} // Disable button during lockout period
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              isLoginDisabled ? 'Try again in 15 minutes' : 'Log In'
            )}
          </button>
        </div>
      </form>
      <ToastContainer />
    </div>
  );
}
